import React, { useEffect, useRef, useState } from "react";
import { useChatContext } from "../../../contexts/ChatContext";
import { useSidebarContext } from "../../../contexts/SidebarContext";
import { useVesselContext } from "../../../contexts/VesselContext";
import { sendMessage } from "../../../hooks/useChatMessages";
import styles from "./ChatMessage.module.css";
import Btn from "../../Btn/Btn";
import { useAppContext } from "../../../contexts/AppContext";
import LeftArrowIcon from "../../Icons/LeftArrowIcon";
import PaperPlaneIcon from "../../Icons/PaperPlaneIcon";
import { removeDiacritics,sanitizeInput } from "../../../utils/textInput";

const ChatMessage: React.FC = () => {
    const { chats, setChats } = useChatContext();
    const { vessels } = useVesselContext();
    const { chatBeingViewedId, setChatBeingViewedId, setContentType } = useSidebarContext();
    const [message, setMessage] = useState("");
    const { showDialog } = useAppContext();
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const messagesListRef = useRef<HTMLDivElement | null>(null);

    const chat = chats[chatBeingViewedId] ?? {
        lastReadTime: 0,
        messages: [],
        contactName: vessels[chatBeingViewedId]?.vesselName ?? "#",
        contactNumber: vessels[chatBeingViewedId]?.contactNumber ?? chatBeingViewedId,
    };

    // Đánh dấu đã đọc khi mở chat
    useEffect(() => {
        if (!chats[chatBeingViewedId]) return;
        setChats(prev => {
            const updated = { ...prev };
            const chat = updated[chatBeingViewedId];
            if (chat.lastInBoundMessageIndex > -1)
                chat.lastReadTime = chat.messages[chat.lastInBoundMessageIndex].messagetime;
            chat.read = true;
            return updated;
        });
    }, [chatBeingViewedId, chat.messages.length]);

    // Scroll đến tin nhắn đầu tiên chưa đọc hoặc cuối cùng
    useEffect(() => {
        if (!chat.messages.length) return;
        const firstUnread = chat.messages.find(m => m.read == false);
        if (firstUnread) {
            const el = document.querySelector(`.${styles["message-container"]}[data-time="${firstUnread.messagetime}"]`);
            if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" });
            else console.log(styles["message-container"]);
        } else if (messagesListRef.current) {
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
        }
    }, [chat.messages.length]);

    const handleSend = async () => {
        if (!message.trim()) return;
        const result = await showDialog("Xác nhận", "Bạn có chắc chắn muốn gửi tin nhắn này?", "confirm");
        if (!result) return;
        const sendTime = Date.now();
        const updated = { ...chats };
        if (!updated[chatBeingViewedId]) {
            updated[chatBeingViewedId] = {
                lastReadTime: 0,
                messages: [],
                contactName: vessels[chatBeingViewedId]?.vesselName ?? "#",
                contactNumber: vessels[chatBeingViewedId]?.contactNumber ?? chatBeingViewedId,
                lastInBoundMessageIndex: -1,
                lastRealMessageIndex: -1,
                read: true,
            };
        }
        const payload = removeDiacritics(message);
        updated[chatBeingViewedId].messages.push({ message: payload, direction: "out", messagetime: sendTime, read: true, timestampcontent: "đang gửi" });
        setChats(updated);
        setMessage("");
        if (messagesListRef.current) {
            setTimeout(() => {
                messagesListRef.current!.scrollTop = messagesListRef.current!.scrollHeight;
            }, 10);
        }
        const res = await sendMessage(chatBeingViewedId, payload);
        if (!res || res.status != 200) {
            setChats(prev => {
                const updated = { ...prev };
                const msg = updated[chatBeingViewedId].messages.find(m => m.messagetime == sendTime);
                if (msg) msg.error = true;
                return updated;
            });
            return;
        }
        const data = await res.json();
        setChats(prev => {
            const updated = { ...prev };
            const msg = updated[chatBeingViewedId].messages.find(m => m.messagetime == sendTime);
            if (msg) {
                msg.messagetime = data.message.time;
                msg.timestampcontent = null;
            }
            return updated;
        });
    };

    return (
        <>
            <div className={styles["chat-header"] + " text"}>
                <Btn className={styles["left-btn"] + " text back-to-chats-list-btn"} title="Quay lại" onClick={() => {
                    setChatBeingViewedId("");
                    setContentType("chatList");
                }}>
                    <LeftArrowIcon />
                </Btn>
                <div className="text"><span className="fw-bolder">{chat.contactName}</span>
                    {"\n"}{chat.contactNumber ?? chatBeingViewedId}
                </div>
            </div>
            <div className={styles["chat-messages"] + " styled-scrollbar"} id={chatBeingViewedId} ref={messagesListRef}>
                {chat.messages.map((message: any, index: number) => {
                    const time = new Date(message.messagetime);
                    return (
                        <div
                            className={styles["message-container"] + (message.direction == "out" ? " " + styles["message-container-sent"] : "")}
                            data-time={message.messagetime}
                            key={message.messagetime}
                        >
                            <div
                                className={
                                    (message.direction == "out" ? styles["message-sent"] : styles["message"]) +
                                    (message.read == true ? "" : " " + styles["message-highlight"]) +
                                    " text" + " position-relative"
                                }
                            >
                                {message.message}
                            </div>
                            <div className={"text-small " + (message.error ? styles["message-timestamp-error"] : styles["message-timestamp"])}>{message.error ? "không thể gửi tin nhắn" : (message.timestampcontent || time.toLocaleString())}</div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
            <div className={styles["chat-input"]}>
                <textarea
                    id="messageInput"
                    className={styles["chat-input-textarea"] + " styled-scrollbar"}
                    placeholder="Nhập tin nhắn..."
                    value={message}
                    maxLength={160}
                    onChange={e => {
                        const val = sanitizeInput(e.target.value || '');
                        setMessage(val.slice(0, 160));
                    }}
                    onPaste={e => {
                        e.preventDefault();
                        const paste = (e.clipboardData || (window as any).clipboardData).getData('text');
                        const sanitized = sanitizeInput(paste || '');
                        setMessage(prev => (prev + sanitized).slice(0, 160));
                    }}
                />
                <Btn className={"text"} title="Gửi" onClick={handleSend}>
                    <PaperPlaneIcon />
                </Btn>
            </div>
        </>
    );
};

export default ChatMessage;
